import { useBreakpoint } from '@/hooks/useBreakpoint';
import { selectUserEmail, selectUserFullname, useAuthStore } from '@/store/authStore';
import { selectClaimsActions, selectClaimsStatus, useClaimsStore } from '@/store/claimsStore';
import {
  selectPoliciesActions,
  selectPoliciesStatus,
  usePoliciesStore,
} from '@/store/policiesStore';
import { segmentIdentify, segmentTrack } from '@/utils/analytics';
import { getPartner } from '@/utils/env';
import { getIsForbiddenError } from '@/utils/error';
import { lazy, memo, useCallback, useEffect } from 'react';

const LogoutDialog = lazy(() =>
  import('./logoutDialog').then(module => ({ default: module.LogoutDialog })),
);

const LogoutDrawer = lazy(() =>
  import('./logoutDrawer').then(module => ({ default: module.LogoutDrawer })),
);

const partner = getPartner();

function SessionTracker() {
  const email = useAuthStore(selectUserEmail);
  const name = useAuthStore(selectUserFullname);
  const { isTabletOrDesktop } = useBreakpoint();

  const claimsStatus = useClaimsStore(selectClaimsStatus);
  const policiesStatus = usePoliciesStore(selectPoliciesStatus);
  const { resetClaimsData, resetClaimsStatus } = useClaimsStore(selectClaimsActions);
  const { resetPoliciesData, resetPoliciesStatus } = usePoliciesStore(selectPoliciesActions);
  const { isAuthenticated, isLogoutDialogVisible, hideLogoutConfirmationDialog, logoutSession } =
    useAuthStore();

  const error = policiesStatus.error || claimsStatus.error;
  const isForbiddenError = getIsForbiddenError(error);

  // Clear policies and claims data, its associated statuses.
  const resetAllData = useCallback(() => {
    resetPoliciesStatus();
    resetClaimsStatus();
    resetPoliciesData();
    resetClaimsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clear current session/data if there are '401' errors.
    if (isForbiddenError) {
      logoutSession();
      resetAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isForbiddenError, resetAllData]);

  useEffect(() => {
    segmentTrack('User Customer Portal Accessed', undefined);

    if (partner) {
      segmentTrack('User Partner Identified', { partner });
    }
  }, []);

  useEffect(() => {
    if (isLogoutDialogVisible) {
      segmentTrack('Logout Button Clicked', undefined);
    }
  }, [isLogoutDialogVisible]);

  useEffect(() => {
    if (email && name) {
      segmentIdentify(email, { email, name });
      return;
    }

    if (email) {
      segmentIdentify(email);
    }
  }, [email, name]);

  const onLogoutCancelClick = () => {
    hideLogoutConfirmationDialog();
  };

  const onLogoutConfirmClick = () => {
    segmentTrack('Logout Last Page Viewed', {
      pagePathname: window.location.pathname,
    });

    // Clear current session/data on confirm.
    logoutSession();
    resetAllData();
  };

  if (!isAuthenticated) {
    return null;
  }

  if (isTabletOrDesktop) {
    return (
      <LogoutDialog
        open={isLogoutDialogVisible}
        onCancelClick={onLogoutCancelClick}
        onActionClick={onLogoutConfirmClick}
      />
    );
  } else {
    return (
      <LogoutDrawer
        open={isLogoutDialogVisible}
        onDrawerClose={onLogoutCancelClick}
        onActionClick={onLogoutConfirmClick}
      />
    );
  }
}

const MemoizedSessionTracker = memo(SessionTracker);

export { MemoizedSessionTracker as SessionTracker };
